import React, { useRef } from "react";
import { Dialog } from "primereact/dialog";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Users, UserUpdateBody } from "../api/models/user.models";

const StyledDialog = styled(Dialog)`
  .p-dialog-content {
    padding: 0;
  }
  .form-container {
    padding: 0 2rem;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  gap: 0.25rem;
  & .p-inputtext,
  .p-password {
    width: 100%;
  }

  & .p-password > div {
    width: 100%;
  }

  & h5 {
    font-weight: 600;
  }
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 1;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
`;

interface EditUserDialogProps {
  show: boolean;
  onHide: () => void;
  updateUser: (data: UserUpdateBody) => Promise<any>;
  updatingUser: boolean;
  user?: Users;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({
  show,
  onHide,
  updateUser,
  updatingUser,
  user,
}) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<UserUpdateBody>({
    defaultValues: {
      id: user?.id || 0,
      names: {
        firstName: user?.names.firstName || "",
        lastName: user?.names.lastName || "",
        name: user?.names.name || "",
      },
      email: { email: user?.email.email || "", isValidated: false },
      phoneNumber: {
        number: user?.phoneNumber?.number || "",
        isValidated: false,
      },
    },
    mode: "onBlur",
  });

  const getFormErrorMessage = (name: string) => {
    // @ts-ignore
    return errors[name] ? (
      <small className="p-error">{errors[name]?.message}</small>
    ) : null;
  };

  const onSubmit = async (data: UserUpdateBody) => {
    const result = await updateUser({
      ...data,
      names: {
        ...data.names,
        name: `${data.names.firstName} ${data.names.lastName}`,
      },
      externalIds: user?.externalIds || null,
    });

    if (result) {
      reset();
      onHide();
    }
  };

  return (
    <StyledDialog
      header={t("header-edit-user")}
      visible={show}
      style={{ width: "600px", minHeight: "400px" }}
      onHide={onHide}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-container">
          <InputContainer>
            <label htmlFor="firstName">{t("label-first-name")}</label>
            <Controller
              name="names.firstName"
              control={control}
              rules={{ required: t("label-required") }}
              render={({ field }) => <InputText id="firstName" {...field} />}
            />
            {getFormErrorMessage("names.firstName")}
          </InputContainer>
          <InputContainer>
            <label htmlFor="lastName">{t("label-last-name")}</label>
            <Controller
              name="names.lastName"
              control={control}
              rules={{ required: t("label-required") }}
              render={({ field }) => <InputText id="lastName" {...field} />}
            />
            {getFormErrorMessage("names.lastName")}
          </InputContainer>
          <InputContainer>
            <label htmlFor="email">{t("label-email")}</label>
            <Controller
              name="email.email"
              control={control}
              rules={{ required: t("label-required") }}
              render={({ field }) => <InputText id="email" {...field} />}
            />
            {getFormErrorMessage("email.email")}
          </InputContainer>
          <InputContainer>
            <label htmlFor="phoneNumber">{t("label-phone-number")}</label>
            <Controller
              name="phoneNumber.number"
              control={control}
              rules={{ required: t("label-required") }}
              render={({ field }) => <InputText id="phoneNumber" {...field} />}
            />
            {getFormErrorMessage("phoneNumber.number")}
          </InputContainer>
        </div>

        <DialogActions>
          <Button
            type="button"
            label={t("cmd-cancel")}
            className="p-button-text"
            onClick={onHide}
          />
          <Button
            type="submit"
            label={t("cmd-save")}
            className="p-ml-2"
            disabled={updatingUser}
            loading={updatingUser}
          />
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default EditUserDialog;
